import {type SelectOptionItem, useTransition} from '@yandex-delivery/blocks'
import type {ServerError} from '@yandex-delivery/core/utils/network'
import {useFormContext} from '@yandex-delivery/form'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {fetchPickUpDropOffPoints} from 'src/shared/api'
import type {LocationDetectedVariant, PickupStation} from 'src/shared/types'

import type {Props} from './types'

export function usePointPickUp({name}: Pick<Props, 'name'>) {
  const names = {
    city: `${name}.city`,
    point: `${name}.point`,
  } as const
  const {watch, setFieldValue} = useFormContext()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const selectedCity = watch(names.city)
  const selectedPoint = watch(names.point)
  const {isPending, points} = useFetchPickUpPoints(name, selectedCity?.geo_id)

  const preparedPoints = useMemo<SelectOptionItem<PickupStation>[]>(() => {
    return points.map(point => ({
      id: point.id,
      label: point.name,
      value: point,
    }))
  }, [points])

  const openModalHandler = useCallback(() => {
    setIsModalVisible(true)
  }, [])

  const closeModalHandler = useCallback(() => {
    setIsModalVisible(false)
  }, [])

  const citySelectHandler = useCallback(
    (city?: LocationDetectedVariant) => {
      setFieldValue(`${name}.city`, city)

      /**
       * @BusinessLogic
       * При смене города необходимо сбрасывать поле "Пункт приема"
       */
      setFieldValue(`${name}.point`, undefined, {shouldValidate: false})
    },
    [name, setFieldValue],
  )

  const pointSelectHandler = useCallback(
    (point?: PickupStation) => {
      setFieldValue(`${name}.point`, point)
    },
    [name, setFieldValue],
  )

  return {
    isModalVisible,
    isPending,
    selectedCity,
    selectedPoint,
    preparedPoints,
    openModalHandler,
    closeModalHandler,
    citySelectHandler,
    pointSelectHandler,
    points,
    names,
  }
}

function useFetchPickUpPoints(key: string, selectedGeoId?: number) {
  const [{isPending, payload: points = []}, startTransition] = useTransition<PickupStation[], ServerError>(undefined, {
    key: `fetch/points/${key}`,
  })

  useEffect(() => {
    if (selectedGeoId) {
      startTransition(() =>
        fetchPickUpDropOffPoints({
          geo_id: selectedGeoId,
          is_yandex_branded: true,
          is_not_branded_partner_station: true,
          type: 'pickup_point',
        }),
      )
    }
  }, [selectedGeoId])

  return {
    isPending,
    points,
  }
}
